import { Box, Checkbox, FormControlLabel, FormGroup, Grid } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { FC } from "react";
import { DateRangeReportFilter, ReportFilterTargetFields } from "../../api/dtos";

interface IDateRangeReportFilterSelectProps {
    applicableFields: ReportFilterTargetFields[],
    dateRangeReportFilters?: DateRangeReportFilter[],
    setDateRangeReportFilters: React.Dispatch<React.SetStateAction<DateRangeReportFilter[] | undefined>>,
    readOnlyDates?: boolean,
}

export const DateRangeReportFilterSelect: FC<IDateRangeReportFilterSelectProps> = ({
    applicableFields,
    dateRangeReportFilters,
    setDateRangeReportFilters,
    readOnlyDates = false,
}) => {
    const matchingFilters = dateRangeReportFilters?.filter(filter =>
        filter.filterTargetField !== undefined && applicableFields.includes(filter.filterTargetField)
    ) ?? [];
    const selectedFilter = matchingFilters[0];

    const updateFilters = (changes: Partial<DateRangeReportFilter>) => {
        setDateRangeReportFilters(current => [
            ...(current ?? []).filter(filter =>
                filter.filterTargetField === undefined || !applicableFields.includes(filter.filterTargetField)
            ),
            ...applicableFields.map(field => {
                const existing = current?.find(filter => filter.filterTargetField === field);
                return new DateRangeReportFilter({
                    ...existing,
                    filterTargetField: field,
                    isStartInclusive: existing?.isStartInclusive ?? true,
                    isEndInclusive: existing?.isEndInclusive ?? true,
                    ...changes,
                });
            }),
        ]);
    };

    return (
        <FormGroup>
            <Grid container gap={2} sx={{ mt: 1 }}>
                <Box>
                    <DatePicker
                        onChange={value => updateFilters({ startDate: value?.toFormat("yyyy-MM-dd") })}
                        label="Start Date"
                        format="dd/MM/yyyy"
                        value={selectedFilter?.startDate ? DateTime.fromISO(selectedFilter.startDate) : null}
                        readOnly={readOnlyDates}
                    />
                    <FormControlLabel
                        sx={{ display: "flex" }}
                        label="Inclusive"
                        control={<Checkbox
                            checked={selectedFilter?.isStartInclusive ?? true}
                            indeterminate={matchingFilters.some(filter =>
                                (filter.isStartInclusive ?? true) !== (selectedFilter?.isStartInclusive ?? true)
                            )}
                            onChange={(_, checked) => updateFilters({ isStartInclusive: checked })}
                        />}
                    />
                </Box>
                <Box>
                    <DatePicker
                        onChange={value => updateFilters({ endDate: value?.toFormat("yyyy-MM-dd") })}
                        label="End Date"
                        format="dd/MM/yyyy"
                        value={selectedFilter?.endDate ? DateTime.fromISO(selectedFilter.endDate) : null}
                        readOnly={readOnlyDates}
                    />
                    <FormControlLabel
                        sx={{ display: "flex" }}
                        label="Inclusive"
                        control={<Checkbox
                            checked={selectedFilter?.isEndInclusive ?? true}
                            indeterminate={matchingFilters.some(filter =>
                                (filter.isEndInclusive ?? true) !== (selectedFilter?.isEndInclusive ?? true)
                            )}
                            onChange={(_, checked) => updateFilters({ isEndInclusive: checked })}
                        />}
                    />
                </Box>
            </Grid>
        </FormGroup>
    );
};
